import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  Card,
  CardContent,
  CardActions,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Switch,
  FormControlLabel,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  DragIndicator as DragIndicatorMuiIcon,
  Add as AddIcon,
} from "@mui/icons-material";
import { validateField } from "../../utils/formSchemaUtils";
import "./FormBuilder.scss";

const FIELD_TYPES = [
  { value: "text", label: "Text Input" },
  { value: "email", label: "Email" },
  { value: "number", label: "Number" },
  { value: "textarea", label: "Text Area" },
  { value: "dropdown", label: "Dropdown" },
  { value: "multiselect", label: "multiselect" },
  { value: "radio", label: "Radio" },
  { value: "boolean", label: "Yes/No" },
  { value: "date", label: "Date DOB" },
  { value: "file", label: "File Upload" },
  { value: "location", label: "Location" },
  { value: "multiSelectCheckbox", label: "Multi Select Checkbox" },
  { value: "gender", label: "Gender" },
];

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", flag: "🇬🇧" },
  { code: "hn", name: "Hinglish", flag: "🇮🇳" },
  { code: "hi", name: "हिंदी (Hindi)", flag: "🇮🇳" },
  { code: "ta", name: "தமிழ் (Tamil)", flag: "🇮🇳" },
  { code: "te", name: "తెలుగు (Telugu)", flag: "🇮🇳" },
  { code: "kn", name: "ಕನ್ನಡ (Kannada)", flag: "🇮🇳" },
  { code: "bn", name: "বাংলা (Bengali)", flag: "🇮🇳" },
  { code: "gu", name: "ગુજરાતી (Gujarati)", flag: "🇮🇳" },
  { code: "mr", name: "मराठी (Marathi)", flag: "🇮🇳" },
];

const generateFieldKey = (label) => {
  return label
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
};

const generateStepId = (title) => {
  return title
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
};

const FormBuilderSteps = ({ initialSteps = [], onStepsChange }) => {
  const [steps, setSteps] = useState(initialSteps.length > 0 ? initialSteps : []);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [showStepDialog, setShowStepDialog] = useState(false);
  const [showFieldDialog, setShowFieldDialog] = useState(false);
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [editingFieldIndex, setEditingFieldIndex] = useState(null);
  const [currentLanguageTab, setCurrentLanguageTab] = useState("en");
  const [draggedFieldIndex, setDraggedFieldIndex] = useState(null);
  const [draggedStepIndex, setDraggedStepIndex] = useState(null);
  const [validationError, setValidationError] = useState("");

  const [currentStep, setCurrentStep] = useState({
    step_id: "",
    title: "",
    title_hi: "",
    title_hn: "",
    title_ta: "",
    title_te: "",
    title_kn: "",
    title_bn: "",
    title_gu: "",
    title_mr: "",
    description: "",
    description_hi: "",
    description_hn: "",
    description_ta: "",
    description_te: "",
    description_kn: "",
    description_bn: "",
    description_gu: "",
    description_mr: "",
    fields: []
  });

  const [currentField, setCurrentField] = useState({
    key: "",
    label: "",
    label_hi: "",
    label_hn: "",
    label_ta: "",
    label_te: "",
    label_kn: "",
    label_bn: "",
    label_gu: "",
    label_mr: "",
    type: "text",
    required: false,
    isFilter: false,
    isVisibleToEmployer: false,
    visibleToEmployerLabel: "",
    isRequiredInRegionFilter: false,
    options: [],
    options_hi: [],
    options_hn: [],
    options_ta: [],
    options_te: [],
    options_kn: [],
    options_bn: [],
    options_gu: [],
    options_mr: [],
  });
  const [optionLabel, setOptionLabel] = useState("");
  const [optionValue, setOptionValue] = useState("");
  const [editingOptionIndex, setEditingOptionIndex] = useState(null);
  const [draggedOptionIndex, setDraggedOptionIndex] = useState(null);

  // Watch for prop changes and update steps
  useEffect(() => {
    if (initialSteps.length > 0) {
      setSteps(initialSteps);
    }
  }, [initialSteps]);

  // Update parent when steps change
  useEffect(() => {
    if (onStepsChange) {
      onStepsChange(steps);
    }
  }, [steps, onStepsChange]);

  // STEP MANAGEMENT FUNCTIONS
  const resetStepForm = () => {
    setCurrentStep({
      step_id: "",
      title: "",
      title_hi: "",
      title_hn: "",
      title_ta: "",
      title_te: "",
      title_kn: "",
      title_bn: "",
      title_gu: "",
      title_mr: "",
      description: "",
      description_hi: "",
      description_hn: "",
      description_ta: "",
      description_te: "",
      description_kn: "",
      description_bn: "",
      description_gu: "",
      description_mr: "",
      fields: []
    });
    setCurrentLanguageTab("en");
    setValidationError("");
  };

  const handleAddStep = () => {
    setEditingStepIndex(null);
    resetStepForm();
    setShowStepDialog(true);
  };

  const handleEditStep = (index) => {
    setEditingStepIndex(index);
    setCurrentStep({ ...steps[index] });
    setCurrentLanguageTab("en");
    setValidationError("");
    setShowStepDialog(true);
  };

  const handleSaveStep = () => {
    if (!currentStep.title) {
      setValidationError("Title is required");
      return;
    }

    if (!currentStep.step_id) {
      setValidationError("Step ID is required");
      return;
    }

    // Check for duplicate step IDs
    const stepIds = editingStepIndex !== null
      ? steps.map((s, i) => i !== editingStepIndex ? s.step_id : null).filter(Boolean)
      : steps.map((s) => s.step_id);

    if (stepIds.includes(currentStep.step_id)) {
      setValidationError("This step ID already exists");
      return;
    }

    let updatedSteps;
    if (editingStepIndex !== null) {
      updatedSteps = [...steps];
      updatedSteps[editingStepIndex] = currentStep;
    } else {
      updatedSteps = [...steps, currentStep];
    }

    setSteps(updatedSteps);
    setShowStepDialog(false);
    resetStepForm();

    // Set current step to the newly added or edited step
    if (editingStepIndex === null) {
      setCurrentStepIndex(updatedSteps.length - 1);
    }
  };

  const handleDeleteStep = (index) => {
    const updatedSteps = steps.filter((_, i) => i !== index);
    setSteps(updatedSteps);
    if (currentStepIndex >= updatedSteps.length) {
      setCurrentStepIndex(Math.max(0, updatedSteps.length - 1));
    }
  };

  const handleStepChange = (key, value) => {
    const updated = { ...currentStep, [key]: value };

    // Auto-generate step_id from English title
    if (key === "title" && !currentStep.step_id) {
      updated.step_id = generateStepId(value);
    }

    setCurrentStep(updated);
    setValidationError("");
  };

  const handleStepDragStart = (index) => {
    setDraggedStepIndex(index);
  };

  const handleStepDrop = (index) => {
    if (draggedStepIndex === null || draggedStepIndex === index) return;

    const updatedSteps = [...steps];
    const draggedStep = updatedSteps[draggedStepIndex];
    updatedSteps.splice(draggedStepIndex, 1);
    updatedSteps.splice(index, 0, draggedStep);

    setSteps(updatedSteps);
    setDraggedStepIndex(null);

    // Update current step index if needed
    if (currentStepIndex === draggedStepIndex) {
      setCurrentStepIndex(index);
    } else if (draggedStepIndex < currentStepIndex && index >= currentStepIndex) {
      setCurrentStepIndex(currentStepIndex - 1);
    } else if (draggedStepIndex > currentStepIndex && index <= currentStepIndex) {
      setCurrentStepIndex(currentStepIndex + 1);
    }
  };

  // FIELD MANAGEMENT FUNCTIONS
  const resetFieldForm = () => {
    setCurrentField({
      key: "",
      label: "",
      label_hi: "",
      label_hn: "",
      label_ta: "",
      label_te: "",
      label_kn: "",
      label_bn: "",
      label_gu: "",
      label_mr: "",
      type: "text",
      required: false,
      isFilter: false,
      isVisibleToEmployer: false,
      visibleToEmployerLabel: "",
      isRequiredInRegionFilter: false,
      options: [],
      options_hi: [],
      options_hn: [],
      options_ta: [],
      options_te: [],
      options_kn: [],
      options_bn: [],
      options_gu: [],
      options_mr: [],
    });
    setCurrentLanguageTab("en");
    setValidationError("");
    setOptionLabel("");
    setOptionValue("");
    setEditingOptionIndex(null);
    setDraggedOptionIndex(null);
  };

  const handleAddField = () => {
    if (steps.length === 0) {
      setValidationError("Please add a step first");
      return;
    }
    setEditingFieldIndex(null);
    resetFieldForm();
    setShowFieldDialog(true);
  };

  const handleEditField = (index) => {
    setEditingFieldIndex(index);
    const currentFields = steps[currentStepIndex]?.fields || [];
    setCurrentField({ ...currentFields[index] });
    setCurrentLanguageTab("en");
    setValidationError("");
    setShowFieldDialog(true);
  };

  const handleSaveField = () => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const currentOptions = currentField[optionsKey] || [];

    if (!currentField.label) {
      setValidationError("Label is required");
      return;
    }

    if (!currentField.key) {
      setValidationError("Key is required");
      return;
    }

    if (["dropdown", "radio", "multiselect", "multiSelectCheckbox", "gender"].includes(currentField.type)) {
      if (!currentOptions || currentOptions.length === 0) {
        setValidationError(`Options are required for ${currentField.type}`);
        return;
      }
    }

    const currentFields = steps[currentStepIndex]?.fields || [];
    const keys = editingFieldIndex !== null
      ? currentFields.map((f, i) => i !== editingFieldIndex ? f.key : null).filter(Boolean)
      : currentFields.map((f) => f.key);

    if (keys.includes(currentField.key)) {
      setValidationError("This key already exists in this step");
      return;
    }

    let updatedFields;
    if (editingFieldIndex !== null) {
      updatedFields = [...currentFields];
      updatedFields[editingFieldIndex] = currentField;
    } else {
      updatedFields = [...currentFields, currentField];
    }

    const updatedSteps = [...steps];
    updatedSteps[currentStepIndex] = {
      ...updatedSteps[currentStepIndex],
      fields: updatedFields
    };

    setSteps(updatedSteps);
    setShowFieldDialog(false);
    resetFieldForm();
  };

  const handleDeleteField = (index) => {
    const currentFields = steps[currentStepIndex]?.fields || [];
    const updatedFields = currentFields.filter((_, i) => i !== index);

    const updatedSteps = [...steps];
    updatedSteps[currentStepIndex] = {
      ...updatedSteps[currentStepIndex],
      fields: updatedFields
    };

    setSteps(updatedSteps);
  };

  const handleFieldDragStart = (index) => {
    setDraggedFieldIndex(index);
  };

  const handleFieldDrop = (index) => {
    if (draggedFieldIndex === null || draggedFieldIndex === index) return;

    const currentFields = steps[currentStepIndex]?.fields || [];
    const updatedFields = [...currentFields];
    const draggedField = updatedFields[draggedFieldIndex];
    updatedFields.splice(draggedFieldIndex, 1);
    updatedFields.splice(index, 0, draggedField);

    const updatedSteps = [...steps];
    updatedSteps[currentStepIndex] = {
      ...updatedSteps[currentStepIndex],
      fields: updatedFields
    };

    setSteps(updatedSteps);
    setDraggedFieldIndex(null);
  };

  const handleFieldChange = (key, value) => {
    const updated = { ...currentField, [key]: value };

    // Reset visibleToEmployerLabel when isVisibleToEmployer is turned off
    if (key === "isVisibleToEmployer" && value === false) {
      updated.visibleToEmployerLabel = "";
    }

    setCurrentField(updated);
    setValidationError("");
  };

  const handleLanguageLabelChange = (value) => {
    const labelKey = currentLanguageTab === "en" ? "label" : `label_${currentLanguageTab}`;
    const updated = { ...currentField, [labelKey]: value };

    setCurrentField(updated);
    setValidationError("");
  };

  const handleAddOption = (label, value) => {
    if (!label.trim()) {
      setValidationError("Option label is required");
      return;
    }
    if (!value.trim()) {
      setValidationError("Option value is required");
      return;
    }

    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const optionObject = {
      label: label.trim(),
      value: value.trim()
    };

    const updated = {
      ...currentField,
      [optionsKey]: [...(currentField[optionsKey] || []), optionObject],
    };

    setCurrentField(updated);
    setOptionLabel("");
    setOptionValue("");
    setValidationError("");
  };

  const handleEditOption = (optionIndex) => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const option = currentField[optionsKey][optionIndex];

    setEditingOptionIndex(optionIndex);
    if (typeof option === 'object') {
      setOptionLabel(option.label);
      setOptionValue(option.value);
    } else {
      setOptionLabel(option);
      setOptionValue(option);
    }
  };

  const handleUpdateOption = () => {
    if (!optionLabel.trim()) {
      setValidationError("Option label is required");
      return;
    }
    if (!optionValue.trim()) {
      setValidationError("Option value is required");
      return;
    }

    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const optionObject = {
      label: optionLabel.trim(),
      value: optionValue.trim()
    };

    const updatedOptions = [...currentField[optionsKey]];
    updatedOptions[editingOptionIndex] = optionObject;

    const updated = {
      ...currentField,
      [optionsKey]: updatedOptions,
    };

    setCurrentField(updated);
    setOptionLabel("");
    setOptionValue("");
    setEditingOptionIndex(null);
    setValidationError("");
  };

  const handleCancelEditOption = () => {
    setEditingOptionIndex(null);
    setOptionLabel("");
    setOptionValue("");
    setValidationError("");
  };

  const handleRemoveOption = (optionIndex) => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const updated = {
      ...currentField,
      [optionsKey]: currentField[optionsKey].filter((_, i) => i !== optionIndex),
    };

    setCurrentField(updated);
    if (editingOptionIndex === optionIndex) {
      setEditingOptionIndex(null);
      setOptionLabel("");
      setOptionValue("");
    }
  };

  const handleOptionDragStart = (index) => {
    setDraggedOptionIndex(index);
  };

  const handleOptionDrop = (index) => {
    if (draggedOptionIndex === null || draggedOptionIndex === index) return;

    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    const options = [...currentField[optionsKey]];
    const draggedOption = options[draggedOptionIndex];

    options.splice(draggedOptionIndex, 1);
    options.splice(index, 0, draggedOption);

    const updated = {
      ...currentField,
      [optionsKey]: options,
    };

    setCurrentField(updated);
    setDraggedOptionIndex(null);
  };

  const getLabelValue = () => {
    if (currentLanguageTab === "en") {
      return currentField.label || "";
    }
    const labelKey = `label_${currentLanguageTab}`;
    return currentField[labelKey] || "";
  };

  const getOptionsValue = () => {
    const optionsKey = currentLanguageTab === "en" ? "options" : `options_${currentLanguageTab}`;
    return currentField[optionsKey] || [];
  };

  const getStepTitle = (step) => {
    return step.title || "(Untitled Step)";
  };

  const getStepDescription = (step) => {
    return step.description || "";
  };

  const currentFields = steps[currentStepIndex]?.fields || [];

  return (
    <Box className="form-builder-container">
      {/* Steps Overview */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
            Form Steps
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddStep}
          >
            Add Step
          </Button>
        </Box>

        {steps.length === 0 ? (
          <Alert severity="info">No steps added yet. Click "Add Step" to create your first step.</Alert>
        ) : (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            {steps.map((step, index) => (
              <Card
                key={index}
                sx={{
                  minWidth: 200,
                  cursor: "pointer",
                  border: currentStepIndex === index ? "2px solid #1976d2" : "1px solid #ddd",
                  bgcolor: currentStepIndex === index ? "#e3f2fd" : "#fff",
                  opacity: draggedStepIndex === index ? 0.5 : 1,
                }}
                draggable
                onDragStart={() => handleStepDragStart(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleStepDrop(index)}
              >
                <CardContent sx={{ pb: 1, "&:last-child": { pb: 1 } }}>
                  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                    <DragIndicatorMuiIcon sx={{ fontSize: "1rem", color: "#999", mt: 0.5 }} />
                    <Box sx={{ flex: 1 }} onClick={() => setCurrentStepIndex(index)}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Step {index + 1}: {getStepTitle(step)}
                      </Typography>
                      {getStepDescription(step) && (
                        <Typography variant="caption" sx={{ color: "#666", display: "block", mb: 0.5 }}>
                          {getStepDescription(step)}
                        </Typography>
                      )}
                      <Chip
                        label={`${step.fields?.length || 0} fields`}
                        size="small"
                        sx={{ fontSize: "0.7rem", height: 20 }}
                      />
                    </Box>
                  </Box>
                </CardContent>
                <CardActions sx={{ pt: 0, justifyContent: "flex-end", gap: 0 }}>
                  <IconButton size="small" onClick={() => handleEditStep(index)} color="primary">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteStep(index)} color="error">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {/* Current Step Fields */}
      {steps.length > 0 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
              {getStepTitle(steps[currentStepIndex])} - Fields
            </Typography>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={handleAddField}
            >
              Add Field
            </Button>
          </Box>

          <Box className="fields-list">
            {currentFields.length === 0 ? (
              <Alert severity="info">No fields in this step. Click "Add Field" to start.</Alert>
            ) : (
              currentFields.map((field, index) => (
                <Card
                  key={index}
                  className="field-card"
                  draggable
                  onDragStart={() => handleFieldDragStart(index)}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleFieldDrop(index)}
                  sx={{
                    opacity: draggedFieldIndex === index ? 0.5 : 1,
                    cursor: "move",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <CardContent sx={{ pb: 1 }}
                    style={{
                      display: "flex",
                      justifyContent: "start",
                      alignItems: "start",
                      // backgroundColor: "#ffceceff",
                      gap: "10px",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                      <DragIndicatorMuiIcon sx={{ fontSize: "1.2rem", color: "#999" }} />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {field.label || "(Untitled)"}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          <span style={{ fontWeight: "700 !important", marginLeft: "5px", border: "1px solid #50c8ffff", borderRadius: "5px", padding: "2px 5px", backgroundColor: "#e0f5ffff" }}> Type: {FIELD_TYPES.find((t) => t.value === field.type)?.label || field.type}</span>
                          {field.required && <span style={{ fontWeight: "700", marginLeft: "5px", border: "1px solid #b50000ff", borderRadius: "5px", padding: "2px 5px", backgroundColor: "#ffebebff" }}>{field.required && " • Required"}</span>}
                        </Typography>
                      </Box>
                    </Box>
                    {field.options && field.options.length > 0 && (
                      <Typography variant="caption" sx={{ display: "block", color: "#0a0a0aff", mt: 0.5, fontWeight: "700 !important", border: "1px solid orange", borderRadius: "5px", padding: "2px 5px", backgroundColor: "#fff4e5" }}>
                        Options: {field.options.map(opt => typeof opt === 'object' ? opt.label : opt).join(", ")}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions sx={{ pt: 0 }}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditField(index)}
                      color="primary"
                      title="Edit"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteField(index)}
                      color="error"
                      title="Delete"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </CardActions>
                </Card>
              ))
            )}
          </Box>
        </Box>
      )}

      {/* Step Dialog */}
      <Dialog
        open={showStepDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowStepDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingStepIndex !== null ? "Edit Step" : "Add New Step"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <TextField
              label="Step ID"
              value={currentStep.step_id}
              onChange={(e) => handleStepChange("step_id", e.target.value)}
              fullWidth
              size="small"
              placeholder="auto_generated_id"
              sx={{ mb: 1.5 }}
            />
          </Box>

          {/* Language Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={currentLanguageTab}
              onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  fontSize: "0.75rem",
                  minWidth: "70px",
                  py: 0.5,
                },
              }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Tab
                  key={lang.code}
                  label={`${lang.flag} ${lang.code.toUpperCase()}`}
                  value={lang.code}
                  title={lang.name}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label={`Title (${currentLanguageTab.toUpperCase()})`}
              value={
                currentLanguageTab === "en"
                  ? currentStep.title
                  : currentStep[`title_${currentLanguageTab}`] || ""
              }
              onChange={(e) =>
                handleStepChange(
                  currentLanguageTab === "en" ? "title" : `title_${currentLanguageTab}`,
                  e.target.value
                )
              }
              fullWidth
              size="small"
              sx={{ mb: 1.5 }}
            />

            <TextField
              label={`Description (${currentLanguageTab.toUpperCase()})`}
              value={
                currentLanguageTab === "en"
                  ? currentStep.description
                  : currentStep[`description_${currentLanguageTab}`] || ""
              }
              onChange={(e) =>
                handleStepChange(
                  currentLanguageTab === "en" ? "description" : `description_${currentLanguageTab}`,
                  e.target.value
                )
              }
              fullWidth
              size="small"
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowStepDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveStep} variant="contained" color="primary">
            {editingStepIndex !== null ? "Update" : "Add"} Step
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Dialog */}
      <Dialog
        open={showFieldDialog}
        onClose={(event, reason) => {
          if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
            setShowFieldDialog(false);
          }
        }}
        disableEscapeKeyDown
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingFieldIndex !== null ? "Edit Field" : "Add New Field"}
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }} style={{ paddingTop: "10px" }}>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
              <InputLabel>Field Type</InputLabel>
              <Select
                value={currentField.type}
                label="Field Type"
                onChange={(e) => handleFieldChange("type", e.target.value)}
              >
                {FIELD_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Language Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
            <Tabs
              value={currentLanguageTab}
              onChange={(e, newValue) => setCurrentLanguageTab(newValue)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  fontSize: "0.75rem",
                  minWidth: "70px",
                  py: 0.5,
                },
              }}
            >
              {SUPPORTED_LANGUAGES.map((lang) => (
                <Tab
                  key={lang.code}
                  label={`${lang.flag} ${lang.code.toUpperCase()}`}
                  value={lang.code}
                  title={lang.name}
                />
              ))}
            </Tabs>
          </Box>

          <Box sx={{ mb: 2 }}>
            <TextField
              label={`Label (${currentLanguageTab.toUpperCase()})`}
              value={getLabelValue()}
              onChange={(e) => handleLanguageLabelChange(e.target.value)}
              fullWidth
              size="small"
              placeholder={`Enter label in ${SUPPORTED_LANGUAGES.find((l) => l.code === currentLanguageTab)?.name}`}
              sx={{ mb: 1.5 }}
            />

            <TextField
              label="Field Key"
              value={currentField.key}
              onChange={(e) => handleFieldChange("key", e.target.value)}
              fullWidth
              size="small"
              placeholder="Enter custom key name"
              sx={{ mb: 1.5 }}
            />

            {["dropdown", "radio", "multiselect", "multiSelectCheckbox", "gender"].includes(currentField.type) && (
              <Box>
                <Typography variant="caption" sx={{ display: "block", mb: 1, fontWeight: 600 }}>
                  Options ({currentLanguageTab.toUpperCase()})
                </Typography>
                <Box sx={{ mb: 1 }}>
                  {getOptionsValue().map((option, idx) => {
                    const optionLabel = typeof option === 'object' ? option.label : option;
                    const optionValue = typeof option === 'object' ? option.value : option;
                    return (
                      <Box
                        key={idx}
                        draggable
                        onDragStart={() => handleOptionDragStart(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={() => handleOptionDrop(idx)}
                        sx={{
                          display: "flex",
                          gap: 1,
                          mb: 0.5,
                          alignItems: "center",
                          p: 0.75,
                          bgcolor: draggedOptionIndex === idx ? "#e0e0e0" : "#f5f5f5",
                          borderRadius: 1,
                          cursor: "move",
                          opacity: draggedOptionIndex === idx ? 0.5 : 1,
                        }}
                      >
                        <DragIndicatorMuiIcon sx={{ fontSize: "1rem", color: "#999" }} />
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {optionLabel}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#666" }}>
                            Value: {optionValue}
                          </Typography>
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleEditOption(idx)}
                          color="primary"
                          title="Edit"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveOption(idx)}
                          color="error"
                          title="Delete"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    );
                  })}
                </Box>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <TextField
                    label="Option Label (User-Facing)"
                    size="small"
                    placeholder="e.g., 1-3 Years"
                    value={optionLabel}
                    onChange={(e) => setOptionLabel(e.target.value)}
                    helperText="This is what users will see"
                  />
                  <TextField
                    label="Option Value (System)"
                    size="small"
                    placeholder="e.g., 1_3_years"
                    value={optionValue}
                    onChange={(e) => setOptionValue(e.target.value)}
                    helperText="This is the value stored in the system"
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    {editingOptionIndex !== null && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleCancelEditOption}
                        sx={{ flex: 1 }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => editingOptionIndex !== null ? handleUpdateOption() : handleAddOption(optionLabel, optionValue)}
                      sx={{ flex: 1 }}
                    >
                      {editingOptionIndex !== null ? "Update Option" : "Add Option"}
                    </Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 2 }}>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Required</InputLabel>
              <Select
                value={currentField.required ? "yes" : "no"}
                label="Required"
                onChange={(e) => handleFieldChange("required", e.target.value === "yes")}
              >
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2, border: "1px solid #cecece", padding: 2, borderRadius: 1 }} >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Employer Settings
            </Typography>

            {currentField.type !== "location" && (<FormControlLabel
              control={
                <Switch
                  checked={currentField.isFilter || false}
                  onChange={(e) => handleFieldChange("isFilter", e.target.checked)}
                  color="primary"
                />
              }
              label="Is Filter"
              sx={{
                "& .MuiFormControlLabel-label": {
                  color: "#333",
                  fontWeight: 500,
                },
              }}
            />
            )}
            <Box sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              p: 1.5,
              bgcolor: "#fafafa"
            }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={currentField.isVisibleToEmployer || false}
                    onChange={(e) => handleFieldChange("isVisibleToEmployer", e.target.checked)}
                    color="primary"
                  />
                }
                label={<p style={{ color: "black", margin: 0 }}>Visible to Employer</p>}
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "#333",
                    fontWeight: 500,
                  },
                  mb: currentField.isVisibleToEmployer ? 1.5 : 0,
                }}
              />

              {currentField.isVisibleToEmployer && (
                <TextField
                  label="Employer Visible Labels"
                  value={currentField.visibleToEmployerLabel || ""}
                  onChange={(e) => handleFieldChange("visibleToEmployerLabel", e.target.value)}
                  fullWidth
                  inputProps={{ maxLength: 40 }}
                  size="small"
                  placeholder="Enter label to show to employer"
                  helperText="This label will be displayed to employers for this field (max 50 characters)"
                />
              )}
            </Box>

            {currentField.type === "location" && (
              <FormControlLabel
                control={
                  <Switch
                    checked={currentField.isRequiredInRegionFilter || false}
                    onChange={(e) => handleFieldChange("isRequiredInRegionFilter", e.target.checked)}
                    color="primary"
                  />
                }
                label="Required in Region Filter"
                sx={{
                  "& .MuiFormControlLabel-label": {
                    color: "#333",
                    fontWeight: 500,
                  },
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFieldDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveField} variant="contained" color="primary">
            {editingFieldIndex !== null ? "Update" : "Add"} Field
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default FormBuilderSteps;
